<template>
  <div class="col-sm-12">

    <!-- Barra de acciones -->
    <div class="d-flex justify-content-between align-items-center mb-3 no-print">
      <button class="btn btn-sm btn-secondary" @click="$router.back()">
        <vue-feather type="arrow-left" size="14" class="me-1" /> Regresar
      </button>
      <div class="btn-group">
        <button class="btn btn-sm btn-primary" :disabled="loading || !data" @click="downloadPDF">
          <vue-feather type="download" size="14" class="me-1" /> Descargar
        </button>
        <button class="btn btn-sm btn-secondary" :disabled="loading || !data" @click="print">
          <vue-feather type="printer" size="14" class="me-1" /> Imprimir
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Cargando póliza...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Documento -->
    <div v-else-if="data" class="card shadow-sm" ref="pdfContent">
      <div class="card-body px-4 py-4" style="font-size: 13px;">

        <!-- ── Membrete ────────────────────────────── -->
        <div class="row align-items-center mb-4">
          <div class="col-6">
            <img :src="require('@/assets/images/logo/logo-login.png')" alt="SIMM" style="height: 48px;">
          </div>
          <div class="col-6 text-end">
            <div class="fw-bold" style="font-size: 15px;">Ficha de Póliza</div>
            <div class="text-muted">Fecha de emisión: {{ hoy }}</div>
            <div class="text-muted">Póliza núm. <strong>{{ data.numeroPoliza || '—' }}</strong></div>
          </div>
        </div>

        <hr class="border-secondary mb-4">

        <!-- ── Datos generales ───────────────────────── -->
        <div class="section-title">Datos Generales</div>
        <div class="row mb-4">
          <div class="col-md-3">
            <div class="field-label">Número de póliza</div>
            <div class="field-value fw-bold">{{ data.numeroPoliza || '—' }}</div>
          </div>
          <div class="col-md-3">
            <div class="field-label">Fecha de solicitud</div>
            <div class="field-value">{{ formatDate(data.fechaSolicitud) }}</div>
          </div>
          <div class="col-md-3">
            <div class="field-label">Status</div>
            <div class="field-value">{{ data.status ?? '—' }}</div>
          </div>
          <div class="col-md-3">
            <div class="field-label">Versión</div>
            <div class="field-value">{{ data.version ?? '—' }}</div>
          </div>
          <div class="col-md-6 mt-2" v-if="data.notas">
            <div class="field-label">Notas</div>
            <div class="field-value">{{ data.notas }}</div>
          </div>
          <div class="col-md-6 mt-2" v-if="data.observaciones">
            <div class="field-label">Observaciones</div>
            <div class="field-value">{{ data.observaciones }}</div>
          </div>
        </div>

        <!-- ── Contratante ───────────────────────────── -->
        <div class="section-title">Contratante</div>
        <div class="row mb-4">
          <div class="col-md-5">
            <div class="field-label">Nombre completo</div>
            <div class="field-value fw-bold">{{ nombreContratante }}</div>
          </div>
          <div class="col-md-2">
            <div class="field-label">RFC</div>
            <div class="field-value">{{ data.rfc || '—' }}</div>
          </div>
          <div class="col-md-3">
            <div class="field-label">CURP</div>
            <div class="field-value">{{ data.curp || '—' }}</div>
          </div>
          <div class="col-md-2">
            <div class="field-label">Fecha de nacimiento</div>
            <div class="field-value">{{ formatDate(data.fechaNacimiento) }}</div>
          </div>

          <!-- Domicilios en línea -->
          <template v-if="data.domicilios && data.domicilios.length">
            <div class="col-12 mt-3">
              <div class="field-label mb-1">Domicilios</div>
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width:90px">Tipo</th>
                    <th>Calle</th>
                    <th style="width:60px">Ext.</th>
                    <th style="width:60px">Int.</th>
                    <th>Colonia</th>
                    <th>Municipio</th>
                    <th>Estado</th>
                    <th style="width:70px">C.P.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(dom, idx) in data.domicilios" :key="idx">
                    <td class="text-capitalize">{{ domTipoNombre(dom) }}</td>
                    <td>{{ dom.calle || '—' }}</td>
                    <td>{{ dom.numeroExt || '—' }}</td>
                    <td>{{ dom.numeroInt || '—' }}</td>
                    <td>{{ dom.colonia || '—' }}</td>
                    <td>{{ dom.municipio || '—' }}</td>
                    <td>{{ dom.estado || '—' }}</td>
                    <td>{{ dom.codigoPostal || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- Medios de contacto -->
          <template v-if="data.mediosContacto && data.mediosContacto.length">
            <div class="col-12 mt-3">
              <div class="field-label mb-1">Medios de contacto</div>
              <div class="d-flex flex-wrap gap-4">
                <div v-for="(m, idx) in data.mediosContacto" :key="idx">
                  <span class="text-muted me-1">{{ m.tipo || m.tipoMedio || `Medio ${idx + 1}` }}:</span>
                  <span>{{ m.descripcion || m.valor || '—' }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- Información financiera -->
          <template v-if="data.informacionFinanciera && data.informacionFinanciera.length">
            <div class="col-12 mt-3">
              <div class="field-label mb-1">Información financiera</div>
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Instrumento</th>
                    <th>Banco</th>
                    <th>Cuenta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(f, idx) in data.informacionFinanciera" :key="idx">
                    <td>{{ f.instrumento || f.instrumentoNombre || '—' }}</td>
                    <td>{{ f.banco || f.bancoNombre || '—' }}</td>
                    <td>{{ f.cuenta || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>

        <!-- ── Producto y cobertura ───────────────────── -->
        <div class="section-title">Producto y Cobertura</div>
        <div class="row mb-4">
          <div class="col-md-3">
            <div class="field-label">Aseguradora</div>
            <div class="field-value fw-bold">{{ data.aseguradoraNombre || '—' }}</div>
          </div>
          <div class="col-md-3">
            <div class="field-label">Producto</div>
            <div class="field-value fw-bold">{{ data.productoNombre || '—' }}</div>
          </div>
          <div class="col-md-2">
            <div class="field-label">Ramo</div>
            <div class="field-value">{{ data.ramo || '—' }}</div>
          </div>
          <div class="col-md-2">
            <div class="field-label">Plazo</div>
            <div class="field-value">{{ data.plazo || '—' }}</div>
          </div>
          <div class="col-md-2">
            <div class="field-label">Tipo</div>
            <div class="field-value">{{ data.tipo || '—' }}</div>
          </div>
          <div class="col-md-2 mt-2">
            <div class="field-label">Divisa</div>
            <div class="field-value">{{ data.divisaSimbolo ? `${data.divisaSimbolo} ` : '' }}{{ data.divisa || '—' }}</div>
          </div>
          <div class="col-md-3 mt-2">
            <div class="field-label">Lugar de trabajo</div>
            <div class="field-value">{{ data.lugarTrabajo || '—' }}</div>
          </div>
          <div class="col-md-3 mt-2">
            <div class="field-label">Enróler</div>
            <div class="field-value">{{ data.enroler || '—' }}</div>
          </div>
        </div>

        <!-- ── Condiciones financieras ────────────────── -->
        <template v-if="condiciones">
          <div class="section-title">Condiciones Financieras</div>
          <div class="row mb-4">
            <div class="col-md-3">
              <div class="field-label">Inicio de vigencia</div>
              <div class="field-value">{{ formatDate(condiciones.inicioVigencia) }}</div>
            </div>
            <div class="col-md-3">
              <div class="field-label">Fin de vigencia</div>
              <div class="field-value">{{ formatDate(condiciones.finVigencia) }}</div>
            </div>
            <div class="col-md-3">
              <div class="field-label">Frecuencia de pago</div>
              <div class="field-value">{{ condiciones.frecuenciaPago || '—' }}</div>
            </div>
            <div class="col-md-3">
              <div class="field-label">Prima neta</div>
              <div class="field-value fw-bold">{{ formatCurrency(condiciones.primaNeta) }}</div>
            </div>
            <div class="col-md-3 mt-2">
              <div class="field-label">IVA</div>
              <div class="field-value">{{ formatCurrency(condiciones.iva) }}</div>
            </div>
            <div class="col-md-3 mt-2">
              <div class="field-label">Prima total</div>
              <div class="field-value fw-bold">{{ formatCurrency(condiciones.primaTotal) }}</div>
            </div>
            <div class="col-md-3 mt-2">
              <div class="field-label">Prima periódica</div>
              <div class="field-value">{{ formatCurrency(condiciones.primaPeriodica) }}</div>
            </div>
            <template v-if="condiciones['$type'] === 'gmm'">
              <div class="col-md-3 mt-2">
                <div class="field-label">Suma asegurada</div>
                <div class="field-value">{{ formatCurrency(condiciones.sumaAsegurada) }}</div>
              </div>
              <div class="col-md-2 mt-2">
                <div class="field-label">Deducible</div>
                <div class="field-value">{{ condiciones.deducible || '—' }}</div>
              </div>
              <div class="col-md-2 mt-2">
                <div class="field-label">Coaseguro</div>
                <div class="field-value">{{ condiciones.coaseguro || '—' }}</div>
              </div>
              <div class="col-md-2 mt-2">
                <div class="field-label">Tope coaseguro</div>
                <div class="field-value">{{ formatCurrency(condiciones.topeCoaseguro) }}</div>
              </div>
              <div class="col-md-2 mt-2">
                <div class="field-label">Derecho de póliza</div>
                <div class="field-value">{{ formatCurrency(condiciones.derechoPoliza) }}</div>
              </div>
            </template>
            <template v-if="condiciones['$type'] === 'vida'">
              <div class="col-md-3 mt-2">
                <div class="field-label">Suma asegurada</div>
                <div class="field-value">{{ formatCurrency(condiciones.sumaAsegurada) }}</div>
              </div>
              <div class="col-md-3 mt-2">
                <div class="field-label">Días contratados</div>
                <div class="field-value">{{ condiciones.diasContratados ?? '—' }}</div>
              </div>
            </template>
          </div>
        </template>

        <!-- ── Asegurados ─────────────────────────────── -->
        <template v-if="data.asegurados && data.asegurados.length">
          <div class="section-title">Asegurados ({{ data.asegurados.length }})</div>
          <div class="table-responsive mb-4">
            <table class="table table-sm table-bordered mb-0">
              <thead class="table-secondary">
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Parentesco</th>
                  <th>Género</th>
                  <th>Edad</th>
                  <th>País</th>
                  <th>Fecha nac.</th>
                  <th>Antigüedad</th>
                  <th>Fecha alta</th>
                  <th>Fecha fin</th>
                  <th class="text-end">Prima</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, idx) in data.asegurados" :key="idx">
                  <td>{{ idx + 1 }}</td>
                  <td class="fw-bold">{{ [a.nombre, a.paterno, a.materno].filter(Boolean).join(' ') }}</td>
                  <td>{{ a.parentesco || '—' }}</td>
                  <td>{{ a.genero || '—' }}</td>
                  <td class="text-center">{{ a.edad ?? '—' }}</td>
                  <td>{{ a.pais || a.paisNombre || '—' }}</td>
                  <td>{{ formatDate(a.fechaNacimiento) }}</td>
                  <td>{{ formatDate(a.antiguedad) }}</td>
                  <td>{{ formatDate(a.fechaAlta) }}</td>
                  <td>{{ formatDate(a.fechaFin) }}</td>
                  <td class="text-end">{{ formatCurrency(a.prima) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── Pie de página ──────────────────────────── -->
        <hr class="border-secondary mt-2 mb-3">
        <div class="text-center text-muted" style="font-size: 11px;">
          Documento generado el {{ hoy }} · SIMM v.2.0
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import api from '@/axios';
import html2pdf from 'html2pdf.js';

const TIPOS_DOMICILIO = { 1: 'Personal', 2: 'Laboral', 3: 'Fiscal' };

export default {
  name: 'DetallePolizaReadOnly',
  data() {
    return {
      data: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    hoy() {
      return new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    },
    nombreContratante() {
      if (!this.data) return '—';
      return [this.data.nombre, this.data.paterno, this.data.materno].filter(Boolean).join(' ') || '—';
    },
    condiciones() {
      if (!this.data?.condiciones) return null;
      let c = this.data.condiciones;
      if (typeof c === 'string') {
        try { c = JSON.parse(c); } catch { return null; }
      }
      return c;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;
        const response = await api.get(`wizard/poliza/${this.$route.params.id}`);
        this.data = response.data;
      } catch {
        this.error = 'No se pudo cargar el detalle de la póliza.';
      } finally {
        this.loading = false;
      }
    },
    domTipoNombre(dom) {
      return TIPOS_DOMICILIO[dom.tipoId] ?? dom.tipo ?? 'Domicilio';
    },
    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d)) return val;
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },
    formatCurrency(value) {
      if (value == null || value === '') return '—';
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    },
    print() {
      window.print();
    },
    async downloadPDF() {
      try {
        const id = this.$route.params.id;
        const fecha = new Date().toISOString().split('T')[0];
        await html2pdf().set({
          margin: [10, 10, 10, 10],
          filename: `Poliza_${id}_${fecha}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait', compress: true },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        }).from(this.$refs.pdfContent).save();
      } catch {
        alert('Hubo un error al generar el PDF.');
      }
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 4px;
  margin-bottom: 14px;
}

.field-label {
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 2px;
}

.field-value {
  font-size: 13px;
  color: #212529;
}

.table th, .table td {
  font-size: 12px;
  vertical-align: middle;
}

@media print {
  .no-print { display: none !important; }
  .card { border: 1px solid #dee2e6 !important; box-shadow: none !important; }
  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  tr { page-break-inside: avoid; }
}
</style>
